import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { convertDataTypeToFeatureType } from "components/paywall/paywallUtils";
import Tabs, { TabItem } from "components/ui/tabs/Tabs";
import { ProHelperDataTypes } from "lib/providers/ProHelper";
import { EventData } from "lib/tracking";
import PaywallCTAModal from "./PaywallCTAModal";
import SubscriptionDowngradeModal from "./SubscriptionDowngradeModal";
import SubscriptionPlanCards from "./SubscriptionPlanCards";
import { StudioType } from "./subscriptionPlanDetailsConfig";
import { SubscriptionPlanInfo } from "./SubscriptionPlanInfo";

const Studio: Record<string, StudioType> = {
  Sales: "sales",
  Bid: "bid",
};

type Props = {
  className?: string;
  triggerBookADemoForm?: (value: boolean) => void;
  trackingData?: EventData;
  defaultOpenTab?: StudioType;
};
function Subscription({
  className,
  triggerBookADemoForm,
  trackingData,
  defaultOpenTab = Studio.Sales,
}: Props): JSX.Element {
  const [paywallCTAModalOpen, setPaywallCTAModalOpen] = useState(false);
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [isAnnualByStudio, setIsAnnualByStudio] = useState<Record<StudioType, boolean>>({
    sales: true,
    bid: true,
  });

  const handleIsAnnualChange = (studioType: StudioType, isAnnual: boolean) => {
    setIsAnnualByStudio((previousState) => ({
      ...previousState,
      [studioType]: isAnnual,
    }));
  };

  const renderStudioTabContent = (studioType: StudioType) => {
    const isAnnual = isAnnualByStudio[studioType];
    return (
      <TabContent>
        <SubscriptionPlanCards
          studioType={studioType}
          triggerBookADemoForm={
            triggerBookADemoForm ? triggerBookADemoForm : setPaywallCTAModalOpen
          }
          triggerDowngradeModal={setDowngradeModalOpen}
          isAnnual={isAnnual}
          onIsAnnualChange={(value) => handleIsAnnualChange(studioType, value)}
        />
        <SubscriptionPlanInfo
          studioType={studioType}
          triggerBookADemoForm={
            triggerBookADemoForm ? triggerBookADemoForm : setPaywallCTAModalOpen
          }
          triggerDowngradeModal={setDowngradeModalOpen}
          isAnnual={isAnnual}
        />
        <PaywallCTAModal
          isOpen={paywallCTAModalOpen}
          onClose={() => setPaywallCTAModalOpen(false)}
          showBookADemoForm={true}
          featureType={
            studioType === Studio.Bid
              ? "BIDS"
              : trackingData?.["Data type"]
                ? convertDataTypeToFeatureType(trackingData?.["Data type"] as ProHelperDataTypes)
                : undefined
          }
        />
        <SubscriptionDowngradeModal
          isOpen={downgradeModalOpen}
          onOpenChange={(open) => setDowngradeModalOpen(open)}
        />
      </TabContent>
    );
  };

  const tabItems: TabItem[] = [
    {
      key: Studio.Sales,
      label: "Sales Studio",
      children: renderStudioTabContent(Studio.Sales),
    },
    {
      key: Studio.Bid,
      label: "Bid Studio",
      children: renderStudioTabContent(Studio.Bid),
    },
  ];

  return (
    <Page className={className}>
      <Title h1>Plans</Title>
      <Tabs
        items={tabItems}
        defaultValue={defaultOpenTab}
        size="large"
        padding="0px"
        margin="0px 0px 0px 24px"
        hasBottomBorder={false}
      />
    </Page>
  );
}

const Page = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const Title = styled(Text)({
  marginLeft: 24,
  marginTop: 24,
});

const TabContent = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  padding: 24,
  gap: 48,
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

export default Subscription;
